import { forwardRef, MouseEventHandler } from 'react'

import Link from '@/routing/Link'
import { Plus } from 'lucide-react'

import { cn } from '@/lib/utils'

import { SIDEBAR_CTA_HEIGHT } from './sidebar.constants'

const SIDEBAR_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'

interface SidebarCreateButtonProps {
  href: string
  label: string
  isCollapsed?: boolean
  className?: string
  dataTestId?: string
  /**
   * Guest lock: looks disabled (`aria-disabled`) but still fires `onAction`.
   * Never a <Link> — callers must pass `onAction` so there is no href to follow.
   */
  lookDisabled?: boolean
  /** Explicit click handler — when provided, renders a <button> instead of a <Link>. */
  onAction?: () => void
  onClick?: MouseEventHandler<HTMLAnchorElement>
}

export const SidebarCreateButton = forwardRef<HTMLAnchorElement, SidebarCreateButtonProps>(
  function SidebarCreateButton(props, ref) {
    const {
      href,
      label,
      isCollapsed = false,
      className,
      dataTestId = 'sidebar-ask-button',
      lookDisabled = false,
      onAction,
      onClick,
      ...rest
    } = props

    const sharedClassName = cn(
      'flex w-full items-center justify-center overflow-hidden rounded-lg text-[var(--sidebar-cta-foreground)] shadow-soft transition-[transform,padding] duration-300 px-4',
      lookDisabled ? 'cursor-not-allowed opacity-50' : 'hover:-translate-y-0.5',
      isCollapsed && 'px-0',
      className,
    )

    const sharedStyle = {
      height: SIDEBAR_CTA_HEIGHT,
      transitionTimingFunction: SIDEBAR_EASE,
      backgroundImage: 'var(--sidebar-cta-gradient)',
    }

    const content = (
      <>
        <Plus className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
        <span
          className={cn(
            'overflow-hidden whitespace-nowrap text-[16px] font-medium leading-5 transition-[max-width,opacity,margin] duration-300',
            isCollapsed
              ? 'ml-0 max-w-0 opacity-0'
              : 'ml-2 max-w-[180px] opacity-100',
          )}
          style={{ transitionTimingFunction: SIDEBAR_EASE }}
        >
          {label}
        </span>
      </>
    )

    if (onAction) {
      return (
        <button
          type="button"
          ref={ref as React.Ref<HTMLButtonElement>}
          data-testid={dataTestId}
          className={sharedClassName}
          style={sharedStyle}
          aria-disabled={lookDisabled || undefined}
          {...rest}
          onClick={(event) => {
            onClick?.(event as never)
            event.preventDefault()
            onAction()
          }}
        >
          {content}
        </button>
      )
    }

    return (
      <Link
        ref={ref}
        href={href}
        data-testid={dataTestId}
        className={sharedClassName}
        style={sharedStyle}
        {...rest}
        onClick={onClick}
      >
        {content}
      </Link>
    )
  },
)
